import { Dispatch, MiddlewareAPI } from '@reduxjs/toolkit';
import { Socket, io } from 'socket.io-client';
import { SocketActions, SocketMessages } from '../utils/constants';
import {
  addChatroom,
  addChatroomDispute,
  addChatroomMessage,
  addImagesToChatroomChat,
  addMatchChat,
  NewAnnouncement,
  unarchiveChatroom,
  updateChatroomDispute,
  updateChatroomSocket,
} from '../slices/inboxSlice';
import { toast } from 'react-toastify';
import { apiSlice } from '../api/apiSlice';
import { storageApiSlice } from '../slices/storageApiSlice';
import { store } from '../store';

const InboxSocketMiddleware = (api: MiddlewareAPI) => {
  let socket: Socket | undefined;
  const url = import.meta.env.VITE_SOCKET_URL + '/inbox';
  type ActionType<T> = { payload: T; type: string };
  return (next: Dispatch) => async (action: any) => {
    switch (action.type) {
      case SocketActions.ConnectInbox:
        api.dispatch(updateChatroomSocket('loading'));
        if (socket) {
          api.dispatch(updateChatroomSocket('connected'));
          break;
        }
        socket = io(url ?? '', { extraHeaders: { token: '' } });
        let { payload } = action as ActionType<{
          id: string;
          rooms?: number[];
        }>;

        socket.on(SocketMessages.ConnectInbox, () => {
          api.dispatch(updateChatroomSocket('connected'));
          const user = api.getState().auth?.user;
          socket?.emit('join-room', `user-${user?.id}`);
        });

        socket.on(SocketMessages.ChatroomMessage, (msg: string) => {
          const parsed = JSON.parse(msg);
          const user = api.getState().auth.user;
          if (parsed.error && parsed.user_id === user.id) {
            toast.error(parsed.error);
          }
          api.dispatch(apiSlice.util.invalidateTags([{ type: 'Chatroom', id: parsed.room }]));
          api.dispatch(
            addChatroomMessage({
              chatroomId: parsed.room,
              message: {
                id: parsed.id,
                user_id: parsed.user_id,
                user_roles: parsed.user_roles,
                username: parsed.username,
                image: parsed.image,
                time: parsed.time,
                message: parsed.message,
                isDeleted: parsed.isDeleted,
              },
            }),
          );
        });

        socket.on(SocketMessages.FirstChatroomMessage, (chatroom: any) => {
          api.dispatch(addChatroom(chatroom));
          api.dispatch(apiSlice.util.invalidateTags([{ type: 'Chatroom', id: 'ALL' }]));
        });

        socket.on(
          SocketMessages.unArchiveChatroom,
          (payload: { chatroomId: number; userId: number }) => {
            api.dispatch(unarchiveChatroom(payload));
          },
        );

        socket.on(SocketMessages.ChatroomDispute, (msg) => {
          api.dispatch(addChatroomDispute(msg));
          api.dispatch(apiSlice.util.invalidateTags(['SingleChatroom', 'Chatroom']));
        });

        socket.on(SocketMessages.ResolveChatroomDispute, (msg) => {
          api.dispatch(updateChatroomDispute(msg));
          api.dispatch(apiSlice.util.invalidateTags(['SingleChatroom']));
        });

        socket.on(SocketMessages.NewMatchChat, (msg: any) => {
          api.dispatch(addMatchChat(msg));
          api.dispatch(apiSlice.util.invalidateTags(['MatchChat']));
        });

        socket.on(
          SocketMessages.AddImagesToChatroomChat,
          (msg: { messageId: number; attachments: string[] }) => {
            api.dispatch(addImagesToChatroomChat(msg));
          },
        );

        socket.on(SocketMessages.FirstMatchChat, (msg: string) => {
          api.dispatch(apiSlice.util.invalidateTags(['MatchChat']));
        });

        socket.on(SocketMessages.NewForfeitAnnouncement, (msg: any) => {
          api.dispatch(NewAnnouncement(msg));
        });

        break;

      case SocketActions.ConnectChatroom:
        if (!socket) break;
        const { payload: cCPayload } = <ActionType<{ id: string }>>action;
        socket?.emit('join-room', `chatroom-${cCPayload?.id}`);
        break;

      case SocketActions.SendChatroomMessage:
        if (!socket) break;
        const { payload: payloadChat } = <ActionType<any>>action;
        let chatPayloads = {
          user_id: payloadChat.user_id,
          user_roles: payloadChat.user_roles,
          username: payloadChat.username,
          image: payloadChat.image,
          room: payloadChat.room,
          message: payloadChat.message,
          time: payloadChat.time,
          attachments: payloadChat.attachments.map((file: File) => file.name.split('.')[0]),
        };
        socket.emit(
          SocketMessages.ChatroomMessage,
          JSON.stringify(chatPayloads),
          async (response: { messageId: number } | { error: string }) => {
            if ('error' in response) {
              console.error('Failed to send chat message:', response.error);
              return;
            }

            const messageId = response.messageId;

            if (!payloadChat.attachments?.length) {
              return;
            }

            const formData = new FormData();
            payloadChat.attachments.forEach((file: File) => {
              formData.append('attachments', file);
            });

            try {
              const urls: { url: string; blobName: string }[] = await store
                .dispatch(storageApiSlice.endpoints.uploadImages.initiate(formData))
                .unwrap();

              const imagePayload = {
                messageId: messageId,
                attachments: urls.map((url) => url.url),
              };

              socket?.emit(SocketMessages.AddImagesToChatroomChat, JSON.stringify(imagePayload));
            } catch (err) {
              console.error('Upload failed:', err);
            }
          },
        );

        break;

      case SocketActions.DisconnectInbox:
        if (!socket) break;
        socket.close();
        socket.off();
        socket = undefined;
        api.dispatch(updateChatroomSocket('disconnected'));
        break;
      default:
        return next(action);
    }
  };
};

export default InboxSocketMiddleware;
